import {
  Button,
  Icon,
  Label,
  Select,
  Tooltip,
  Badge,
  LegacyStack,
  TextField,
  FormLayout,
  TextContainer,
} from "@shopify/polaris";
import { XIcon } from "@shopify/polaris-icons";
import React, { useCallback } from "react";
import { formatMoney, assertNumber, getCurrencySymbol } from "../../../helper_functions/utils";
import type { CostAdjustmentEditable, PurchaseOrderState } from "../../../api_utils/types";

const COST_ITEMS = [
  "Custom duties",
  "Discount",
  "Foreign transaction fee",
  "Freight fee",
  "Insurance",
  "Rush fee",
  "Surcharge",
  "Shipping",
  "Other",
];

function CostAdjustments({
  currency,
  lineTotal = 0,
  receivedTotal = 0,
  purchaseOrder,
  setIsDirty,
  costAdjustments,
  setCostAdjustments,
  disabled,
  total,
}: {
  readonly currency: string;
  readonly lineTotal: number;
  readonly receivedTotal?: number;
  readonly purchaseOrder: {
    id: number;
    supplier?: { minimum_order_value: number };
    vendorSettings?: { minimum_order_value: number };
    state: PurchaseOrderState;
  };
  readonly setIsDirty: (isDirty: boolean) => void;
  readonly costAdjustments: CostAdjustmentEditable[];
  readonly setCostAdjustments: (costAdjustments: CostAdjustmentEditable[]) => void;
  readonly disabled: boolean;
  readonly total: number;
}): React.JSX.Element {
  const removeCostAdjustment = useCallback((index: number) => (): void => {
    const clonedCostAdjustments = costAdjustments.map((_item, i) => {
      if (i === index) {
        return {
          ..._item,
          _destroy: true,
        };
      }
      return _item;
    });
    setCostAdjustments(clonedCostAdjustments);
    setIsDirty(true);
  }, [costAdjustments, setCostAdjustments, setIsDirty]);

  const handleCostLabelChange =
    useCallback((index: number) =>
    (label: string): void => {
      const newCostAdjustments = costAdjustments.map((costAdjustment, i) => {
        if (i === index) {
          return {
            ...costAdjustment,
            label,
          };
        }
        return costAdjustment;
      });
      setCostAdjustments(newCostAdjustments);
      setIsDirty(true);
    }, [costAdjustments, setCostAdjustments, setIsDirty]);

  const handleCostAmountChange =
    useCallback((index: number) =>
    (amount: string): void => {
      const cleanAmount = amount?.replace(/[^\d.-]/g, "");
      const newCostAdjustments = costAdjustments.map((costAdjustment, i) => {
        if (i === index) {
          return {
            ...costAdjustment,
            amount: cleanAmount,
          };
        }
        return costAdjustment;
      });

      setCostAdjustments(newCostAdjustments);
      setIsDirty(true);
    }, [costAdjustments, setCostAdjustments, setIsDirty]);

  const handleAddAdjustmentClick = useCallback((): void => {
    const clonedCostAdjustments: CostAdjustmentEditable[] = [
      ...costAdjustments,
      { label: "Other", amount: "", purchase_order_id: purchaseOrder.id },
    ];
    setCostAdjustments(clonedCostAdjustments);
    setIsDirty(true);
  }, [costAdjustments, purchaseOrder.id, setCostAdjustments, setIsDirty]);

  const [taxCostAdjustment] = costAdjustments;
  const subTotal = assertNumber(lineTotal) + assertNumber(taxCostAdjustment.amount);
  
  const minimumOrderValue = purchaseOrder?.supplier?.minimum_order_value ||
    purchaseOrder?.vendorSettings?.minimum_order_value ||
    0;
  const minimumOrderValueWarning = total > 0 && total < minimumOrderValue;

  const changeTaxAmount = handleCostAmountChange(0);

  let totalDisplay: string | React.ReactElement = formatMoney(total, currency, 2);
  const receivedTotalDisplay = formatMoney(receivedTotal, currency, 2);
  if (purchaseOrder.state !== ("draft" as PurchaseOrderState) && receivedTotal !== total) {
    totalDisplay = (
      <Tooltip content={`${receivedTotalDisplay} received of ${totalDisplay} total`}>
        <span>{`${receivedTotalDisplay} (${totalDisplay})`}</span>
      </Tooltip>
    );
  }

  return (
    <FormLayout>
      <FormLayout.Group>
        <LegacyStack alignment="center">
          <LegacyStack.Item fill>
            <Label id="taxesHeading">Taxes</Label>
          </LegacyStack.Item>
          <LegacyStack.Item>
            <div className="w120">
              <TextField
                align="right"
                autoComplete="off"
                id="tax"
                label=""
                labelHidden
                onChange={changeTaxAmount}
                placeholder="0.00"
                prefix={getCurrencySymbol(currency)}
                readOnly={disabled}
                type="currency"
                value={taxCostAdjustment.amount}
              />
            </div>
          </LegacyStack.Item>
          <LegacyStack.Item>
            <div style={{ width: "22px" }} />
          </LegacyStack.Item>
        </LegacyStack>
      </FormLayout.Group>
      <FormLayout.Group>
        <LegacyStack>
          <LegacyStack.Item fill>
            <Label id="subtotalHeading">Subtotal</Label>
          </LegacyStack.Item>
          <LegacyStack>
            <LegacyStack.Item>
              <TextContainer>{formatMoney(subTotal, currency, 2)}</TextContainer>
            </LegacyStack.Item>
            <LegacyStack.Item>
              <div style={{ width: "32px" }} />
            </LegacyStack.Item>
          </LegacyStack>
        </LegacyStack>
      </FormLayout.Group>
      {costAdjustments.map((costAdjustment, index) => (
        index !== 0 && !costAdjustment._destroy && (
          <FormLayout.Group key={costAdjustment.id}>
            <LegacyStack alignment="center" distribution="leading" spacing="tight">
              <LegacyStack.Item>
                <Select
                  disabled={disabled}
                  id="description"
                  label=""
                  labelHidden
                  onChange={handleCostLabelChange(index)}
                  options={COST_ITEMS.map((item) => ({ label: item, value: item, key: item }))}
                  value={costAdjustment.label}
                />
              </LegacyStack.Item>
              <LegacyStack.Item fill />
              <LegacyStack alignment="center" wrap={false}>
                <LegacyStack.Item>
                  <div className="w120">
                    <TextField
                      align="right"
                      autoComplete="off"
                      id="monetaryValue"
                      inputMode="numeric"
                      label=""
                      labelHidden
                      onChange={handleCostAmountChange(index)}
                      placeholder="0.00"
                      prefix={getCurrencySymbol(currency)}
                      readOnly={disabled}
                      type="currency"
                      value={costAdjustment.amount}
                    />
                  </div>
                </LegacyStack.Item>
                <LegacyStack.Item>
                  <Button
                    disabled={disabled}
                    icon={<Icon source={XIcon} />}
                    key="3"
                    onClick={removeCostAdjustment(index)}
                    variant="plain"
                  />
                </LegacyStack.Item>
              </LegacyStack>
            </LegacyStack>
          </FormLayout.Group>
        )
      ))}
      <Button disabled={disabled} onClick={handleAddAdjustmentClick} variant="plain">
        Add adjustment
      </Button>
      <LegacyStack alignment="center" distribution="leading">
        <LegacyStack.Item>
          <Label id="totalHeading">Total</Label>
        </LegacyStack.Item>
        <LegacyStack.Item fill />
        <LegacyStack.Item>
          <TextContainer>{totalDisplay}</TextContainer>
        </LegacyStack.Item>
        <LegacyStack.Item>
          <div style={{ width: "32px" }} />
        </LegacyStack.Item>
      </LegacyStack>
      {minimumOrderValueWarning ? (
        <Tooltip content="Total is less than minimum order value">
          <Badge tone="attention">
            {`Total bill is less than minimum order value ${formatMoney(minimumOrderValue, currency, 2)}`}
          </Badge>
        </Tooltip>
      ) : null}
    </FormLayout>
  );
}

export default CostAdjustments;
